/**
 * ModalManager - centralized modal handling
 * Eliminates scattered modal show/hide logic throughout app.ts
 */

/**
 * Modal configuration
 */
export interface ModalConfig {
  /** The modal element ID */
  id: string;
  /** Optional callback when modal is shown */
  onShow?: () => void;
  /** Optional callback when modal is hidden */
  onHide?: () => void;
  /** Whether clicking the backdrop should close the modal (default: true) */
  closeOnBackdrop?: boolean;
}

/**
 * Modal registration info
 */
interface ModalInfo {
  element: HTMLElement;
  config: ModalConfig;
}

/**
 * Centralized modal manager for showing/hiding modals
 */
export class ModalManager {
  private modals: Map<string, ModalInfo> = new Map();
  private openModals: Set<string> = new Set();

  /**
   * Register a modal for management
   */
  register(config: ModalConfig): void {
    const element = document.getElementById(config.id);
    if (!element) {
      console.warn(`Modal element not found: ${config.id}`);
      return;
    }

    this.modals.set(config.id, { element, config });

    // Set up backdrop handler if enabled. Uses mousedown (not click) so a
    // text-selection drag that starts inside the modal and releases over the
    // backdrop doesn't close it — click fires on the common ancestor of
    // mousedown/mouseup, which would be the backdrop in that case.
    if (config.closeOnBackdrop !== false) {
      element.addEventListener('mousedown', (e: MouseEvent) => {
        if (e.target === element) {
          this.hide(config.id);
        }
      });
    }

    // Set up close button handlers
    element.querySelectorAll('.modal-close-btn').forEach(btn => {
      btn.addEventListener('click', () => this.hide(config.id));
    });
  }

  /**
   * Show a modal by ID
   */
  show(id: string): boolean {
    const modal = this.modals.get(id);
    if (!modal) {
      console.warn(`Modal not registered: ${id}`);
      return false;
    }

    modal.element.classList.remove('hidden');
    this.openModals.add(id);
    modal.config.onShow?.();
    return true;
  }

  /**
   * Hide a modal by ID
   */
  hide(id: string): boolean {
    const modal = this.modals.get(id);
    if (!modal) {
      return false;
    }

    modal.element.classList.add('hidden');
    this.openModals.delete(id);
    modal.config.onHide?.();
    return true;
  }

  /**
   * Toggle a modal's visibility
   */
  toggle(id: string): boolean {
    if (this.isOpen(id)) {
      return this.hide(id);
    }
    return this.show(id);
  }

  /**
   * Check if a modal is open
   */
  isOpen(id: string): boolean {
    return this.openModals.has(id);
  }

  /**
   * Hide all open modals
   */
  hideAll(): void {
    for (const id of this.openModals) {
      this.hide(id);
    }
  }

  /**
   * Get the modal element by ID
   */
  getElement(id: string): HTMLElement | null {
    return this.modals.get(id)?.element ?? null;
  }

  /**
   * Check if any modal is open
   */
  hasOpenModals(): boolean {
    return this.openModals.size > 0;
  }

  /**
   * Get list of open modal IDs
   */
  getOpenModals(): string[] {
    return Array.from(this.openModals);
  }
}

/**
 * Common modal IDs used in the application
 */
export const MODAL_IDS = {
  CREATE_FILE: 'create-file-modal',
  CREATE_COMPONENT: 'create-component-modal',
  DELETE: 'delete-modal',
  RENAME: 'rename-modal',
  HISTORY: 'history-modal',
  MEDIA: 'media-modal',
  TEMPLATE: 'template-modal',
  DOCX_EXPORT: 'docx-export-modal',
  HELP: 'help-modal',
  AUTH: 'auth-modal',
  LOADING: 'loading-modal',
  PROJECT_SWITCHER: 'project-switcher-modal',
  PROJECT_SEARCH: 'project-search-modal',
  SAVE_ERROR: 'save-error-modal',
} as const;

/**
 * Singleton modal manager instance
 */
export const modalManager = new ModalManager();
